import { useLingui } from "@lingui/react";
import { ElementType, Intent } from "@mobility-item-page/utilities/client";
import type { WarpModal } from "@warp-ds/elements/modal";
import { Button } from "@warp-ds/elements/react/button";
import { Modal, ModalHeader } from "@warp-ds/elements/react/modal";
import { useRef, useState } from "react";
import { useAdViewMeta } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import usePulse from "src/utils/pulse-tracking/usePulse.ts";
import { Icon } from "../Icon.tsx";
import { SocialMediaGroup } from "./SocialMediaGroup.tsx";

type Props = {
  variant?: "link" | "button";
};

export const ShareButton = ({ variant = "button" }: Props) => {
  const { i18n } = useLingui();

  const { trackClick } = usePulse();

  const modalRef = useRef<WarpModal>(null);
  const { adId } = useAdViewMeta();
  const { mountOrigin, isPreview } = useAppConfig();
  const shareLink = `${mountOrigin}/mobility/item/${adId}`;

  const toggleModal = async () => {
    // Tracking event: CE_ev55
    trackClick({
      name: "Share ad",
      objectId: "CE_ev55",
      elementType: ElementType.Button,
      intent: Intent.Open,
    });
    modalRef.current?.open();
  };

  const closeModal = () => {
    setCopied(false);
    modalRef.current?.close();
  };

  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    // Tracking event: CE_ev56
    trackClick({
      name: "Share ad",
      objectId: "CE_ev56",
      elementType: ElementType.CopyLink,
      intent: Intent.Share,
    });
    // Note: It will not work for http
    await navigator?.clipboard?.writeText(shareLink);
    if (!copied) {
      setCopied(true);
    }
  };

  const texts = {
    title: i18n.t({ id: `itemPageLayout.sharing.title`, message: "Share link" }),
    shareAd: i18n.t({ id: `itemPageLayout.sharing.shareAd`, message: "Share ad" }),
    copyLink: i18n.t({ id: `itemPageLayout.sharing.copyLink`, message: "Copy link" }),
    copyDone: i18n.t({ id: `itemPageLayout.sharing.copyDone`, message: "Copied" }),
  };

  return (
    <>
      {variant === "link" && (
        <div className="font-normal">
          <Button variant="link" onClick={toggleModal} disabled={isPreview}>
            <span className="flex flex-row items-center gap-8">
              <Icon name="Share" /> {texts.shareAd}
            </span>
          </Button>
        </div>
      )}
      {variant === "button" && (
        <Button
          variant="utilityQuiet"
          onClick={toggleModal}
          aria-label={texts.shareAd}
          className="part-[base]:p-12!"
          disabled={isPreview}
        >
          <Icon name="Share" size="medium" className="block" />
        </Button>
      )}

      <Modal ref={modalRef} onHidden={closeModal}>
        <ModalHeader slot="header" title={texts.title} />
        <div slot="content">
          <p>{shareLink}</p>
          <div className="flex items-center justify-between">
            <SocialMediaGroup />
            <Button
              variant="primary"
              onClick={copyLink}
              data-testid="copy-link-button"
              aria-label={copied ? "" : texts.copyLink}
            >
              {copied ? (
                <div className="flex items-center">
                  <span className="mr-8">{texts.copyDone}</span>
                  <Icon name="Check" />
                </div>
              ) : (
                <span>{texts.copyLink}</span>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
